import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";

const DEFAULT_COLORS = {
  user_bg: "#E8D9D5",
  user_maroon: "#2B0000",
  user_maroon_deep: "#1A0000",
  user_gold: "#D4AF37",
  user_gold_soft: "#D4AF87",
  user_text: "#2B0000",
  user_text_soft: "#685E59",
  user_card: "#FFFAF5",
  user_border: "#E5D2C7",
  user_cream: "#F5ECE7",
  button_text: "#F5ECE7",
};

const COLOR_FIELDS = [
  { key: "user_bg", label: "Body Background", helper: "Main user-side page background." },
  { key: "user_maroon", label: "Dark Maroon", helper: "Navbar, footer, primary buttons and strong accents." },
  { key: "user_maroon_deep", label: "Deep Maroon", helper: "Darker gradients and hover states." },
  { key: "user_gold", label: "Gold Accent", helper: "Icons, lines, badges and highlights." },
  { key: "user_gold_soft", label: "Soft Gold", helper: "Luxury headings and subtle accent text." },
  { key: "user_text", label: "Main Text", helper: "Primary user-side text color." },
  { key: "user_text_soft", label: "Soft Text", helper: "Descriptions, muted labels and helper text." },
  { key: "user_card", label: "Card Background", helper: "Cards, panels and product surfaces." },
  { key: "user_border", label: "Border Color", helper: "Dividers, rules and card borders." },
  { key: "user_cream", label: "Cream Text", helper: "Text used on dark maroon surfaces." },
  { key: "button_text", label: "Button Text", helper: "Text color inside primary buttons." },
];

const ThemeColorSettings = () => {
  const { theme, updateTheme } = useTheme();
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    setColors({
      ...DEFAULT_COLORS,
      ...Object.fromEntries(
        Object.keys(DEFAULT_COLORS).map((key) => [key, theme?.[key] || DEFAULT_COLORS[key]])
      ),
    });
  }, [theme]);

  const previewStyle = useMemo(
    () => ({
      "--preview-bg": colors.user_bg,
      "--preview-maroon": colors.user_maroon,
      "--preview-deep": colors.user_maroon_deep,
      "--preview-gold": colors.user_gold,
      "--preview-gold-soft": colors.user_gold_soft,
      "--preview-text": colors.user_text,
      "--preview-soft": colors.user_text_soft,
      "--preview-card": colors.user_card,
      "--preview-border": colors.user_border,
      "--preview-cream": colors.user_cream,
      "--preview-button-text": colors.button_text,
    }),
    [colors]
  );

  const setColor = (key, value) => {
    setColors((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: "", text: "" });

    const result = await updateTheme({
      ...colors,
      button_bg: colors.user_maroon,
      button_hover: colors.user_maroon_deep,
    });

    setSaving(false);
    setMessage(
      result?.success
        ? { type: "success", text: "User-side theme colors updated successfully." }
        : { type: "error", text: result?.message || "Unable to update colors." }
    );
  };

  const handleReset = () => {
    setColors(DEFAULT_COLORS);
    setMessage({ type: "", text: "" });
  };

  return (
    <div className="min-h-screen bg-[#f6f2ec] p-5 sm:p-7">
      <div className="mx-auto max-w-6xl">
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#b29063]">
            User Side Only
          </p>
          <h1 className="mt-2 font-serif text-4xl font-semibold text-[#2B0000]">
            Theme Colors
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Update the public website colors without touching the admin panel.
          </p>
        </div>

        {message.text && (
          <div
            className={`mb-5 rounded-xl border px-4 py-3 text-sm font-semibold ${
              message.type === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-800"
            }`}
          >
            {message.text}
          </div>
        )}

        <div className="grid gap-6 xl:grid-cols-[1fr_0.9fr]">
          <section className="rounded-2xl border border-[#e3d7ca] bg-white p-5 shadow-sm">
            <div className="grid gap-4 sm:grid-cols-2">
              {COLOR_FIELDS.map((field) => (
                <label key={field.key} className="rounded-xl border border-slate-200 p-4">
                  <span className="block text-sm font-bold text-slate-900">{field.label}</span>
                  <span className="mt-1 block text-xs leading-5 text-slate-500">{field.helper}</span>
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="color"
                      value={colors[field.key]}
                      onChange={(event) => setColor(field.key, event.target.value)}
                      className="h-11 w-14 cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
                    />
                    <input
                      type="text"
                      value={colors[field.key]}
                      onChange={(event) => setColor(field.key, event.target.value)}
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 px-3 py-2 font-mono text-sm outline-none focus:border-[#b29063]"
                    />
                  </div>
                </label>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-xl bg-[#2B0000] px-6 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-[#1A0000] disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save User Theme"}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={saving}
                className="rounded-xl border border-[#d8c6b7] bg-white px-6 py-3 text-sm font-bold text-[#2B0000] transition hover:bg-[#fff7ef]"
              >
                Reset Defaults
              </button>
            </div>
          </section>

          <section className="rounded-2xl border border-[#e3d7ca] bg-white p-5 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">Live User Preview</h2>
            <div
              className="mt-4 overflow-hidden rounded-2xl border"
              style={{ ...previewStyle, backgroundColor: "var(--preview-bg)", borderColor: "var(--preview-border)" }}
            >
              <div className="flex items-center justify-between px-5 py-4" style={{ backgroundColor: "var(--preview-bg)" }}>
                <div className="rounded-full px-5 py-2 text-sm font-bold" style={{ backgroundColor: "var(--preview-maroon)", color: "var(--preview-cream)" }}>
                  Home
                </div>
                <div className="h-12 w-12 rounded-full border" style={{ borderColor: "var(--preview-gold)" }} />
              </div>

              <div className="p-6">
                <p className="text-xs font-bold uppercase tracking-[0.32em]" style={{ color: "var(--preview-gold-soft)" }}>
                  Signature
                </p>
                <h3 className="mt-2 font-serif text-5xl leading-none" style={{ color: "var(--preview-text)" }}>
                  Gold Stories
                </h3>
                <p className="mt-4 max-w-md text-sm leading-7" style={{ color: "var(--preview-soft)" }}>
                  This preview uses the same variables that the public website receives.
                </p>

                <div className="mt-6 rounded-xl border p-4" style={{ backgroundColor: "var(--preview-card)", borderColor: "var(--preview-border)" }}>
                  <p className="font-serif text-2xl" style={{ color: "var(--preview-maroon)" }}>
                    Product Card
                  </p>
                  <div className="mt-2 h-px" style={{ backgroundColor: "var(--preview-gold)" }} />
                  <button
                    type="button"
                    className="mt-5 rounded-full px-5 py-3 text-sm font-bold"
                    style={{ backgroundColor: "var(--preview-maroon)", color: "var(--preview-button-text)" }}
                  >
                    Explore Collection
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ThemeColorSettings;
